import clsx from 'clsx';
import { type ReactElement, useState } from 'react';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import type { MinimalCard } from '@/services/cardDatabase';

interface DraftPoolViewProps {
  pool: MinimalCard[];
}

const COLOR_GROUPS = [
  { key: 'W', label: 'White' },
  { key: 'U', label: 'Blue' },
  { key: 'B', label: 'Black' },
  { key: 'R', label: 'Red' },
  { key: 'G', label: 'Green' },
  { key: 'M', label: 'Multi' },
  { key: 'C', label: 'Colorless' },
] as const;

function getColorGroup(card: MinimalCard): string {
  if (card.color_identity.length === 0) return 'C';
  if (card.color_identity.length > 1) return 'M';
  return card.color_identity[0];
}

export default function DraftPoolView({ pool }: DraftPoolViewProps): ReactElement {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const grouped = new Map<string, MinimalCard[]>();
  for (const card of pool) {
    const group = getColorGroup(card);
    const existing = grouped.get(group) ?? [];
    existing.push(card);
    grouped.set(group, existing);
  }

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (pool.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-xs text-zinc-500">Drafted cards appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Pool ({pool.length})</h3>

      {COLOR_GROUPS.map(({ key, label }) => {
        const cards = grouped.get(key);
        if (!cards || cards.length === 0) return null;
        const isCollapsed = collapsedGroups.has(key);

        return (
          <div key={key}>
            <button
              onClick={() => toggleGroup(key)}
              className="flex w-full items-center gap-1.5 py-1 text-left text-xs text-zinc-400 hover:text-zinc-200"
            >
              <span className={clsx('transition-transform', isCollapsed ? '' : 'rotate-90')}>▶</span>
              <span className="font-medium">{label}</span>
              <span className="ml-auto rounded bg-zinc-700/50 px-1.5 text-[10px]">{cards.length}</span>
            </button>

            {!isCollapsed && (
              <div className="grid grid-cols-3 gap-1.5 pl-3">
                {cards.map((card, i) => (
                  <CardHoverPreview key={`${card.id}-${i}`} imageUris={card.image_uris} name={card.name}>
                    <CardImage imageUris={card.image_uris} name={card.name} size="small" className="rounded" />
                  </CardHoverPreview>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
